import React from "react";

/**
 * Card surface for confirmations opened with modal(..., { raw: true }) — raw
 * modals render no background of their own, so the content must bring one.
 */
export const ConfirmCard = ({ children }) => (
  <div className="flex w-[min(92vw,30rem)] flex-col gap-kit-16 rounded-kit-xl border-[1px] border-[var(--inyc-modal-border-color)] bg-[var(--inyc-primary-light)] p-kit-24 text-left text-[var(--inyc-primary-text-color)] shadow-kit-lg">
    {children}
  </div>
);

/**
 * Confirmation for roster actions that release a performer's upcoming slots
 * (business removing a member, artist leaving a roster).
 * commitments: rows from the roster commitments endpoints.
 * message: sentence lead-in; the release summary ("reopen 2 booked slots…") is appended.
 */

const groupByEvent = (commitments) => {
  const groups = new Map();
  for (const item of commitments) {
    const key = String(item.eventId);
    if (!groups.has(key)) groups.set(key, { key, eventName: item.eventName, items: [] });
    groups.get(key).items.push(item);
  }
  return [...groups.values()];
};

export const summarizeCommitments = (commitments = []) => {
  const booked = commitments.filter((item) => item.status === "accepted").length;
  const applied = commitments.length - booked;
  return [
    booked > 0 && `reopen ${booked} booked ${booked === 1 ? "slot" : "slots"}`,
    applied > 0 && `withdraw ${applied} pending ${applied === 1 ? "application" : "applications"}`,
  ]
    .filter(Boolean)
    .join(" and ");
};

const RosterCommitmentsConfirm = ({
  title,
  message,
  emptyMessage,
  confirmLabel = "confirm",
  commitments = [],
  onCancel,
  onConfirm,
}) => {
  const hasCommitments = commitments.length > 0;

  return (
    <ConfirmCard>
      <div className="flex flex-col gap-kit-6">
        <div className="font-['Primary-Medium'] text-kit-base">{title}</div>
        <div className="text-kit-xs text-[var(--inyc-primary-text-color-light)]">
          {hasCommitments ? `${message} ${summarizeCommitments(commitments)}.` : emptyMessage}
        </div>
      </div>

      {hasCommitments && (
        <div className="flex max-h-[16rem] flex-col gap-kit-16 overflow-y-auto rounded-kit-md border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] px-kit-16 py-kit-12">
          {groupByEvent(commitments).map((group) => (
            <div key={group.key} className="flex flex-col gap-kit-6">
              <div className="truncate text-kit-2xs uppercase tracking-wide text-[var(--inyc-primary-text-color-light)]">
                {group.eventName}
              </div>
              {group.items.map((item) => (
                <div key={item.applicationId} className="flex items-center justify-between gap-kit-12">
                  <div className="min-w-0">
                    <div className="truncate font-['Primary-Medium'] text-kit-xs">{item.slotTitle}</div>
                    <div className="truncate text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
                      {[item.dateLabel, item.timeLabel].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <span
                    className={`shrink-0 rounded-kit-full px-kit-8 py-kit-2 text-kit-2xs font-['Primary-Medium'] ${
                      item.status === "accepted"
                        ? "bg-success-soft text-success"
                        : "bg-[var(--inyc-accent)]/20 text-[var(--inyc-accent)]"
                    }`}
                  >
                    {item.status === "accepted" ? "booked" : "applied"}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-kit-12">
        <button
          onClick={onCancel}
          className="flex-1 py-kit-10 rounded-[var(--inyc-button-border-radius)] border-[1px] border-[var(--inyc-input-border-color)] text-[var(--inyc-primary-text-color-light)] font-['Primary-Medium'] text-kit-base hover:bg-[var(--inyc-primary)] transition-colors duration-150"
        >
          cancel
        </button>
        <button
          onClick={onConfirm}
          className="flex-1 py-kit-10 rounded-[var(--inyc-button-border-radius)] bg-danger-strong text-on-fill font-['Primary-Medium'] text-kit-base hover:bg-danger-strong-hover transition-colors duration-150"
        >
          {confirmLabel}
        </button>
      </div>
    </ConfirmCard>
  );
};

export default RosterCommitmentsConfirm;
